import { DragDropContext, Draggable, DropResult } from "@hello-pangea/dnd";
import { Box, List } from "@mui/material";
import { StrictModeDroppable } from "@/components/common/verticalDraggableList/verticalDraggableList";
import { VIOLET } from "@/config/mui/colorPalette";
import { SelectedService } from "./selectedService";
import { Service } from "./types";

type Props = {
  services: Service[];
  onRemoveService: (index: number) => void;
  onReorderServices: (sourceIndex: number, destinationIndex: number) => void;
  onEditProvider: (index: number) => void;
};

export const SortableServices = ({
  services,
  onRemoveService,
  onReorderServices,
  onEditProvider,
}: Props) => {
  const onDragEnd = (result: DropResult) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    onReorderServices(result.source.index, result.destination.index);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <StrictModeDroppable>
        {(provided, snapshot) => (
          <Box sx={{ position: "relative" }}>
            <List
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{
                p: 0,
              }}
            >
              {services.map((service, index) => (
                <Draggable
                  key={service.serviceMenuItem.id}
                  draggableId={service.serviceMenuItem.id}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <SelectedService
                      ref={provided.innerRef}
                      draggableProps={provided.draggableProps}
                      dragHandleProps={provided.dragHandleProps}
                      isDragging={snapshot.isDragging}
                      service={service.serviceMenuItem}
                      provider={service.provider}
                      onRemove={() => {
                        onRemoveService(index);
                      }}
                      onEditProvider={() => {
                        onEditProvider(index);
                      }}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
            <DraggingBorder isDraggingOver={snapshot.isDraggingOver} />
          </Box>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );
};

// We show border in separate div instead of List component because all list elements
// have a bottom margin (they have to, because skipping margin-bottom for the last element
// messes up the layout during dragging). We want to cut last element's bottom margin,
// hence the bottom: "12px"
const DraggingBorder = ({ isDraggingOver }: { isDraggingOver: boolean }) => {
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        bottom: "12px",
        pointerEvents: "none",
        border: isDraggingOver
          ? `1px solid ${VIOLET[40]}`
          : "1px solid transparent",
        transition: "border 0.2s ease-in-out",
        borderRadius: "4px",
      }}
    />
  );
};
